<template>
  <SelectRoot v-bind="forwarded">
    <SelectTrigger
      v-bind="$attrs"
      :class="[
        'form-select select-trigger',
        props.size && `form-select-${props.size}`,
        arrowWhite && 'form-select-arrow-white',
      ]"
    >
      <SelectValue :placeholder="props.placeholder" :class="valueClass" />
    </SelectTrigger>

    <SelectPortal>
      <SelectContent position="popper" class="dropdown-menu select-scroll-content">
        <SelectScrollUpButton class="select-scroll-button">
          <i class="bx bxs-chevron-up" />
        </SelectScrollUpButton>
        <SelectViewport class="select-scroll-viewport">
          <slot />
        </SelectViewport>
        <SelectScrollDownButton class="select-scroll-button">
          <i class="bx bxs-chevron-down" />
        </SelectScrollDownButton>
      </SelectContent>
    </SelectPortal>
  </SelectRoot>
</template>

<script setup>
import { computed } from 'vue'
import {
  SelectContent,
  SelectPortal,
  SelectRoot,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectTrigger,
  SelectValue,
  SelectViewport,
  useForwardPropsEmits,
} from 'radix-vue'
import { SIZES } from '@shared/constants/bootstrap'

defineOptions({
  inheritAttrs: false,
})

/* eslint-disable vue/require-default-prop */
const props = defineProps({
  autocomplete: { type: String, required: false },
  defaultOpen: { type: Boolean, required: false },
  defaultValue: { type: [String, Number], required: false },
  dir: { type: String, required: false },
  disabled: { type: Boolean, required: false },
  modelValue: { type: [String, Number], required: false },
  name: { type: String, required: false },
  open: { type: Boolean, required: false },
  placeholder: { type: String, required: false },
  required: { type: Boolean, required: false },
  arrowWhite: { type: Boolean, required: false },
  valueClass: { type: String, required: false },
  size: {
    type: String,
    required: false,
    validator: (val) => SIZES.includes(val),
  },
})

const delegatedProps = computed(() => {
  const { placeholder, size, arrowWhite, ...delegated } = props

  return delegated
})

const emits = defineEmits(['update:modelValue', 'update:open'])

const forwarded = useForwardPropsEmits(delegatedProps, emits)
</script>

<style lang="scss">
.select-trigger {
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;

  &:not(.form-select-sm):not(.form-select-lg) {
    min-height: 2.375rem;
  }
  &.form-select-sm {
    min-height: 1.875rem;
  }
  &.form-select-lg {
    min-height: 3rem;
  }
}

.dropdown-menu.select-scroll-content {
  position: relative;
  min-width: 8rem;
  max-height: 24rem;
  z-index: 1060; // greather than modal backgdrop (1050)

  &[data-side='top'] {
    transform: translateY(-.25rem);
  }

  &[data-side='bottom'] {
    transform: translateY(.25rem);
  }

  &[data-state='open'] {
    animation: slide-in-from-top-2 .15s, fade-in 0.15s linear;
  }
}

.select-scroll-viewport {
  width: 100%;
  min-width: calc(
    var(--radix-select-trigger-width) - var(--gu-dropdown-border-width) * 2
  );
  height: var(--radix-select-trigger-height);
}

.select-scroll-button {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 1.5rem;
  background-color: var(--gu-body-bg);
  color: var(--gu-body-color);
  cursor: default;
}

.form-select {
  &.form-select-arrow-white {
    --gu-form-select-bg-img: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23dee2e6' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e");
  }
}
</style>
